import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react';
import FieldRenderer from './FieldRenderer';
import { evaluateCondition } from './conditions';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_RE = /^https?:\/\/.+/i;

function validateComponent(component, value) {
  if (component.required) {
    const empty = value === undefined || value === null || value === ''
      || (Array.isArray(value) && value.length === 0)
      || (component.type === 'checkbox' && value !== true);
    if (empty) return 'This field is required';
  }
  if (value === undefined || value === null || value === '') return null;

  if (component.type === 'email' && !EMAIL_RE.test(value)) return 'Enter a valid email address';
  if (component.type === 'url' && !URL_RE.test(value)) return 'Enter a valid URL (including https://)';
  if (component.minLength && String(value).length < component.minLength) {
    return `Must be at least ${component.minLength} characters`;
  }
  if (component.maxLength && String(value).length > component.maxLength) {
    return `Must be at most ${component.maxLength} characters`;
  }
  if (component.pattern) {
    try {
      if (!new RegExp(component.pattern).test(value)) return component.patternMessage || 'Invalid format';
    } catch {
      // a malformed regex authored in the builder shouldn't crash the renderer
    }
  }
  if (component.type === 'number') {
    const num = Number(value);
    if (component.min !== undefined && component.min !== '' && num < Number(component.min)) {
      return `Must be at least ${component.min}`;
    }
    if (component.max !== undefined && component.max !== '' && num > Number(component.max)) {
      return `Must be at most ${component.max}`;
    }
  }
  return null;
}

function initialValues(pages) {
  const values = {};
  pages.forEach((page) => {
    (page.components || []).forEach((c) => {
      if (c.defaultValue !== undefined && c.defaultValue !== '') values[c.id] = c.defaultValue;
    });
  });
  return values;
}

// Draft persistence is opt-in via `persistKey` — the builder's preview modal
// and canvas render this same component constantly with throwaway/rapidly
// changing schemas, so only the public form filler (which passes a stable
// per-form key) gets localStorage writes.
function loadDraft(persistKey) {
  if (!persistKey) return null;
  try {
    const raw = window.localStorage.getItem(persistKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function saveDraft(persistKey, draft) {
  if (!persistKey) return;
  try {
    window.localStorage.setItem(persistKey, JSON.stringify(draft));
  } catch {
    // private browsing / quota exceeded — losing the draft is fine, it's just a convenience
  }
}
function clearDraft(persistKey) {
  if (!persistKey) return;
  try {
    window.localStorage.removeItem(persistKey);
  } catch {
    // ignore
  }
}

/**
 * Schema-driven renderer: takes a FormVersion's `schema` (pages/components)
 * and renders a fully working, submittable form. This same component is
 * meant to back the builder's live preview later too — it never assumes
 * it's the only thing on the page (no full-page chrome, no fetch calls of
 * its own).
 */
export default function FormRenderer({ schema, antiSpam, onSubmit, onUploadFile, onPageChange, submitting, submitted, successMessage, persistKey }) {
  const pages = schema?.pages || [];
  const theme = schema?.theme || {};
  const i18n = schema?.settings?.i18n;
  const locales = i18n?.enabled ? (i18n.locales || []) : [];
  const draft = useRef(loadDraft(persistKey)).current;
  const [pageIndex, setPageIndex] = useState(() => draft?.pageIndex ?? 0);
  const lastReportedPage = useRef(null);
  const [values, setValues] = useState(() => ({ ...initialValues(pages), ...(draft?.values || {}) }));
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [locale, setLocale] = useState(i18n?.defaultLocale || locales[0]?.code || 'en');

  // Overlays this locale's translated strings (label/placeholder/helpText/
  // options/checkboxLabel) onto the component's base (authoring-language)
  // values — a component with no translation entry for this locale, or a
  // locale with no i18n set up at all, renders exactly as authored.
  const translate = (component) => {
    const t = i18n?.translations?.[locale]?.[component.id];
    if (!t) return component;
    return {
      ...component,
      label: t.label || component.label,
      placeholder: t.placeholder ?? component.placeholder,
      helpText: t.helpText ?? component.helpText,
      checkboxLabel: t.checkboxLabel ?? component.checkboxLabel,
      options: t.options?.length ? component.options.map((opt, i) => ({ ...opt, label: t.options[i] || opt.label })) : component.options,
    };
  };
  const translatePage = (page) => {
    if (!page) return page;
    const t = i18n?.translations?.[locale]?.[`page:${page.id}`];
    return t?.title ? { ...page, title: t.title } : page;
  };

  const currentPage = translatePage(pages[pageIndex]);
  const visiblePageIndices = pages
    .map((p, i) => i)
    .filter((i) => evaluateCondition(pages[i].condition, values));
  const visiblePos = visiblePageIndices.indexOf(pageIndex);
  const isLastPage = visiblePos === visiblePageIndices.length - 1;

  const visibleComponents = (page) => (page?.components || [])
    .filter((c) => evaluateCondition(c.condition, values))
    .map(translate);
  const inputComponents = (page) => visibleComponents(page)
    .filter((c) => c.type !== 'heading' && c.type !== 'paragraph' && c.type !== 'hidden');

  const themeStyle = {
    '--form-primary': theme.primaryColor || '#0176D3',
    '--form-radius': theme.borderRadius != null ? `${theme.borderRadius}px` : '4px',
    fontFamily: theme.fontFamily || undefined,
    backgroundColor: theme.backgroundColor || undefined,
  };

  useEffect(() => {
    if (!onPageChange || submitted || !pages.length) return;
    if (lastReportedPage.current === pageIndex) return;
    lastReportedPage.current = pageIndex;
    onPageChange(pageIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, submitted]);

  const setValue = (id, val) => {
    setValues((prev) => ({ ...prev, [id]: val }));
    if (errors[id]) setErrors((prev) => ({ ...prev, [id]: null }));
  };

  // Autosaves on every field change / page navigation, not just periodically
  // — a respondent who closes the tab mid-fill shouldn't lose anything typed
  // since the last keystroke. Cleared once the submission actually succeeds.
  useEffect(() => {
    if (!persistKey || submitted) return;
    saveDraft(persistKey, { values, pageIndex, savedAt: Date.now() });
  }, [persistKey, values, pageIndex, submitted]);

  useEffect(() => {
    if (submitted) clearDraft(persistKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted]);

  const validatePage = (page) => {
    const pageErrors = {};
    inputComponents(page).forEach((c) => {
      const err = validateComponent(c, values[c.id]);
      if (err) pageErrors[c.id] = err;
    });
    setErrors((prev) => ({ ...prev, ...pageErrors }));
    return Object.keys(pageErrors).length === 0;
  };

  const goNext = () => {
    if (!validatePage(currentPage)) return;
    const nextIndex = visiblePageIndices[visiblePos + 1];
    if (nextIndex !== undefined) setPageIndex(nextIndex);
  };
  const goPrev = () => {
    const prevIndex = visiblePageIndices[visiblePos - 1];
    if (prevIndex !== undefined) setPageIndex(prevIndex);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validatePage(currentPage)) return;
    setSubmitError('');
    try {
      await onSubmit(values, antiSpam ? { honeypot, renderedAt: antiSpam.renderedAt } : undefined);
    } catch (err) {
      setSubmitError(err?.response?.data?.message || err.message || 'Something went wrong — please try again.');
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-10">
        <CheckCircle size={40} className="mx-auto mb-3 text-[#04844B]" />
        <p className="text-[15px] font-bold text-[#181818]">{successMessage || 'Thank you — your response has been recorded.'}</p>
      </div>
    );
  }

  if (!pages.length) {
    return <p className="text-[13px] text-[#706E6B]">This form has no fields configured yet.</p>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" style={themeStyle}>
      {theme.showLogo && theme.logoUrl && (
        <img src={theme.logoUrl} alt="" className="h-10 max-w-[160px] object-contain mb-2" />
      )}
      {locales.length > 1 && (
        <div className="flex justify-end -mb-2">
          <select
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
            className="text-[12px] border border-[#D8DDE6] rounded-[4px] px-2 py-1 text-[#181818] bg-white"
            aria-label="Language"
          >
            {locales.map((l) => <option key={l.code} value={l.code}>{l.label || l.code}</option>)}
          </select>
        </div>
      )}
      {antiSpam?.honeypotField && (
        <input
          type="text"
          name={antiSpam.honeypotField}
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex="-1"
          autoComplete="off"
          aria-hidden="true"
          style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
        />
      )}
      {visiblePageIndices.length > 1 && (
        <div className="flex items-center gap-2 mb-2">
          {visiblePageIndices.map((_, pos) => (
            <div
              key={pos}
              className="h-1.5 flex-1 rounded-full transition-colors"
              style={{ backgroundColor: pos <= visiblePos ? 'var(--form-primary)' : '#D8DDE6' }}
            />
          ))}
        </div>
      )}

      {currentPage.title && (
        <h2 className="text-[16px] font-bold text-[#181818]">{currentPage.title}</h2>
      )}

      <div className="flex flex-wrap gap-4">
        {visibleComponents(currentPage).map((component) => (
          <div
            key={component.id}
            className={component.width === 'half' ? 'w-full sm:w-[calc(50%-0.5rem)]' : 'w-full'}
          >
            <FieldRenderer
              component={component}
              value={values[component.id]}
              error={errors[component.id]}
              onChange={(val) => setValue(component.id, val)}
              onUploadFile={onUploadFile}
            />
          </div>
        ))}
      </div>

      {submitError && (
        <p className="text-[12px] text-[#EA001E] bg-[rgba(234,0,30,0.06)] border border-[rgba(234,0,30,0.15)] rounded-[4px] px-3 py-2">
          {submitError}
        </p>
      )}

      <div className="flex items-center justify-between pt-2">
        {visiblePos > 0 ? (
          <button
            type="button"
            onClick={goPrev}
            className="flex items-center gap-1.5 px-4 py-2 text-[13px] font-bold text-[#706E6B] hover:text-[#181818] transition-colors"
          >
            <ChevronLeft size={16} /> Back
          </button>
        ) : <span />}

        {isLastPage ? (
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2.5 rounded-[var(--form-radius,4px)] text-white text-[13px] font-bold disabled:opacity-60 transition-opacity hover:opacity-90"
            style={{ backgroundColor: 'var(--form-primary)' }}
          >
            {submitting ? 'Submitting…' : (theme.submitLabel || 'Submit')}
          </button>
        ) : (
          <button
            type="button"
            onClick={goNext}
            className="flex items-center gap-1.5 px-6 py-2.5 rounded-[var(--form-radius,4px)] text-white text-[13px] font-bold transition-opacity hover:opacity-90"
            style={{ backgroundColor: 'var(--form-primary)' }}
          >
            Next <ChevronRight size={16} />
          </button>
        )}
      </div>
    </form>
  );
}
